import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faBan, faBell, faWifi, faTimesCircle, faVolumeUp, faVolumeMute } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import OrgContext from '../context/OrgContext'; // Import OrgContext from its file

const OrderManagement = () => {
    const { user } = useContext(OrgContext);
    const [orders, setOrders] = useState([]);
    const [websocketConnected, setWebsocketConnected] = useState(false);
    const audioRef = useRef(null);
    const [isSoundEnabled, setIsSoundEnabled] = useState(true);
    const [locationName, setLocationName] = useState('');

    const location = useLocation();
    const locationID = new URLSearchParams(location.search).get('locationID');
    const wsUrl = `${process.env.REACT_APP_WS}?pid=${locationID}`;
    const token = sessionStorage.getItem('token');

    useEffect(() => {
        if (user && user.locationIDs && locationID) {
            const locationObject = user.locationIDs.find(loc => loc._id === locationID);
            setLocationName(locationObject ? locationObject.name : 'Unknown Location');
        }
    }, [user, locationID]);

    const fetchOrders = useCallback(async () => {
        if (!locationID) return;
    
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/orders/pending`, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    lid: locationID
                }
            });
    
            if (response.data.success && Array.isArray(response.data.orders)) {
                const mappedOrders = response.data.orders.map(order => ({
                    ...order,
                    products: order.products.map(product => ({ 
                        ...product, 
                        isChecked: false 
                    })),
                    status: order.status || 'new',
                    elapsedTime: order.orderTime 
                        ? Math.floor((new Date() - new Date(order.orderTime)) / 1000) 
                        : 0
                }));
                setOrders(mappedOrders);
            } else {
                console.error('Invalid response format:', response.data);
            }
        } catch (error) {
            console.error('Error fetching orders:', error.response?.data || error.message);
        }
    }, [locationID]);
    

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    useEffect(() => {
        let ws;
        
        const connectWebSocket = () => {
            ws = new WebSocket(wsUrl, ['token', token]);

            ws.onopen = () => {
                console.log('WebSocket connected');
                setWebsocketConnected(true);
            };

            ws.onmessage = (event) => {
                try {
                    const order = JSON.parse(event.data);
                    order.elapsedTime = order.orderTime ? Math.floor((new Date() - new Date(order.orderTime)) / 1000) : 0;
            
                    setOrders((prevOrders) => {
                        const index = prevOrders.findIndex(o => o._id === order._id);

                        if (order.status === 'served' || order.status === 'dismissed') {
                            return prevOrders.filter(o => o._id !== order._id);
                        } else if (index !== -1) {
                            return prevOrders.map(o => o._id === order._id ? {
                                ...o,
                                ...order,
                                products: order.products.map(product => {
                                    const existingArticle = o.products.find(a => a._id === product._id);
                                    return existingArticle ? { ...product, isChecked: existingArticle.isChecked } : { ...product, isChecked: false };
                                })
                            } : o);
                        } else {
                            if (isSoundEnabled && order.status === 'pending' && audioRef.current) {
                                audioRef.current.play().catch((error) => {
                                    console.error('Audio play failed:', error);
                                });
                            }
                            return [...prevOrders, {
                                ...order,
                                products: order.products.map(product => ({ ...product, isChecked: false }))
                            }];
                        }
                    });
                } catch (error) {
                    console.error('Error processing incoming order:', error);
                }
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
            };

            ws.onclose = () => {
                console.log('WebSocket disconnected');
                setWebsocketConnected(false);
                setTimeout(connectWebSocket, 5000);
            };
        };

        connectWebSocket();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && (!ws || ws.readyState === WebSocket.CLOSED)) {
                connectWebSocket();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            ws.close();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isSoundEnabled, fetchOrders, wsUrl, token]);

    useEffect(() => {
        audioRef.current = new Audio(`${process.env.REACT_APP_SRV}/uploads/notifikacija.mp3`);
        audioRef.current.preload = 'auto';
    }, []);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setOrders((prevOrders) => prevOrders.map(order => ({
                ...order,
                elapsedTime: order.elapsedTime + 1
            })));
        }, 1000);

        return () => clearInterval(intervalId);
    }, []);

    const formatTime = (seconds) => {
        const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
        const secs = String(seconds % 60).padStart(2, '0');
        return `${mins}:${secs}`;
    };

    const toggleCheck = (orderId, productID) => {
        setOrders(orders => orders.map(order => {
            if (order._id === orderId) {
                const updatedOrder = {
                    ...order,
                    products: order.products.map(product => {
                        if (product._id === productID) {
                            return { ...product, isChecked: !product.isChecked };
                        }
                        return product;
                    })
                };
                return updatedOrder;
            }
            return order;
        }));
    };

    const markOrderReady = async (orderId) => {
        try {
            await axios.put(`${process.env.REACT_APP_SRV}/orders/${orderId}/ready`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrders(orders => orders.map(order =>
                order._id === orderId ? { ...order, status: 'ready' } : order
            ));
        } catch (error) {
            console.error('Error marking order as ready:', error);
        }
    };

    const markOrderServed = async (orderId) => {
        try {
            await axios.put(`${process.env.REACT_APP_SRV}/orders/${orderId}/served`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch (error) {
            console.error('Error marking order as served:', error);
        }
    };
    
    const dismissOrder = async (orderId) => {
        try {
            await axios.put(`${process.env.REACT_APP_SRV}/orders/${orderId}/dismiss`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch (error) {
            console.error('Error dismissing order:', error);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-gray-900">
            <div className="bg-gray-800 w-full py-4 px-8 fixed top-0 z-10 flex justify-between items-center">
                <div className="text-white flex items-center space-x-4">
                    <span className="font-bold">{locationName}</span>
                    <span>{dayjs().format('MMM D, YYYY')}</span>
                </div>
                <h1 className="text-2xl font-bold text-white text-center flex-grow">ORDERS</h1>
                <div className="flex items-center">
                    <button onClick={() => setIsSoundEnabled(!isSoundEnabled)} className="mr-4">
                        <FontAwesomeIcon icon={isSoundEnabled ? faVolumeUp : faVolumeMute} className="text-2xl text-gray-400" />
                    </button>
                    <FontAwesomeIcon icon={websocketConnected ? faWifi : faTimesCircle} className={`text-2xl ${websocketConnected ? 'text-green-500' : 'text-red-500'}`} />
                </div>
            </div>

            <div className="flex-grow pt-20 px-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {
                        orders.map((order) => (
                            <div key={order._id} className="bg-gray-800 border border-gray-200 shadow-lg rounded-lg p-6 flex flex-col justify-between">
                                <div>
                                    <div className="flex justify-between items-baseline mb-2">
                                        <div className="flex items-baseline">
                                            <h2 className="text-xl font-semibold text-white mr-4">#{String(order.ticket).padStart(4, '0')}</h2>
                                            <p className="text-lg font-bold text-white">{formatTime(order.elapsedTime)}</p>
                                    </div>
                                    <span className="text-sm font-medium bg-blue-100 text-blue-800 px-3 py-1 rounded-full">{order.terminal}</span>
                                </div>
                                <p className="font-medium text-gray-300 mb-4 capitalize">{order.status}</p>
                                <ul>
                                    {order.products.map((product) => (
                                        <li key={product._id} className="flex items-center mb-2">
                                            <button
                                                onClick={() => toggleCheck(order._id, product._id)}
                                                className={`h-6 w-6 rounded-full border-2 ${product.isChecked ? 'bg-green-500 border-green-500' : 'bg-gray-300 border-gray-300'}`}
                                                aria-checked={product.isChecked}
                                            />
                                            <span className="text-lg ml-2 text-white">
                                                {product.product.name} × {product.quantity}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="flex justify-between mt-4">
                                {order.status === 'pending' && order.products.every(product => product.isChecked) && (
                                    <button
                                        onClick={() => markOrderReady(order._id)}
                                        className="px-4 py-2 text-white rounded-full bg-orange-500 flex items-center">
                                        <FontAwesomeIcon icon={faCheck} className="mr-2" /> Ready
                                    </button>
                                )}
                                {order.status === 'ready' && (
                                    <button
                                        onClick={() => markOrderServed(order._id)}
                                        className="px-4 py-2 text-white rounded-full bg-green-500 flex items-center">
                                        <FontAwesomeIcon icon={faBell} className="mr-2" /> Serve
                                    </button>
                                )}
                                <button
                                    onClick={() => dismissOrder(order._id)}
                                    className="px-4 py-2 text-white bg-red-500 rounded-full flex items-center ml-auto">
                                    <FontAwesomeIcon icon={faBan} className="mr-2" /> Dismiss
                                </button>
                            </div>
                        </div>
                    ))
                }
            </div>
        </div>
    </div>
);
};

export default OrderManagement;